// components/Pictorial.js
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Lottie from "lottie-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import designAnimation from "../assets/design.json";

import pictorial1 from "../assets/img1.jpeg";
import pictorial2 from "../assets/img2.jpeg";
import pictorial3 from "../assets/img3.jpeg";
import pictorial4 from "../assets/img4.jpeg";
import pictorial5 from "../assets/img5.jpeg";
import pictorial6 from "../assets/img6.jpeg";
import pictorial7 from "../assets/img7.jpeg";
import pictorial8 from "../assets/img8.png";

gsap.registerPlugin(ScrollTrigger);

const Pictorial = () => {
  const sectionRef = useRef(null);
  const heroRef = useRef(null);
  const heroTextWrapRef = useRef(null);
  const heroTitleRef = useRef(null);
  const heroTextRef = useRef(null);
  const lottieWrapRef = useRef(null);

  const galleryRef = useRef(null);
  const galleryShellRef = useRef(null);
  const imageStageRef = useRef(null);
  const activeFrameRef = useRef(null);
  const progressRef = useRef(null);

  const waterCanvasRef = useRef(null);
  const pointerRef = useRef({
    x: 0,
    y: 0,
    targetX: 0,
    targetY: 0,
    active: false,
  });
  const ripplesRef = useRef([]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const images = useMemo(
    () => [
      {
        id: 1,
        src: pictorial1,
        title: "Focused Work",
        category: "Behind the Build",
        description:
          "Moments of deep work, planning, and execution behind digital products.",
      },
      {
        id: 2,
        src: pictorial2,
        title: "Creative Process",
        category: "Design & Direction",
        description:
          "Exploring visual structure, interface decisions, and product experience.",
      },
      {
        id: 3,
        src: pictorial3,
        title: "Technology in Motion",
        category: "Software Engineering",
        description:
          "Building systems with clarity, scalability, and purpose.",
      },
      {
        id: 4,
        src: pictorial4,
        title: "Cyber Mindset",
        category: "Cybersecurity",
        description:
          "Security thinking, analysis, and digital resilience in practice.",
      },
      {
        id: 5,
        src: pictorial5,
        title: "Training & Impact",
        category: "Community & Learning",
        description:
          "Teaching, mentoring, and using technology to empower people.",
      },
      {
        id: 6,
        src: pictorial6,
        title: "Product Vision",
        category: "Strategy",
        description:
          "Turning early ideas into structured, usable, and valuable products.",
      },
      {
        id: 7,
        src: pictorial7,
        title: "Digital Craft",
        category: "Portfolio",
        description:
          "A collection of visual moments around software, branding, and delivery.",
      },
      {
        id: 8,
        src: pictorial8,
        title: "Building Forward",
        category: "Future Work",
        description:
          "A visual story of growth, execution, and continuous improvement.",
      },
    ],
    []
  );

  const goToImage = useCallback((index) => {
    setActiveIndex((index + images.length) % images.length);
  }, [images.length]);

  const goNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const goPrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  /*
    HERO WATER / PIXEL-SHADER STYLE BACKGROUND
    No extra package.
    Canvas is intentionally low-resolution, then scaled up with image-rendering.
    This gives the background that premium pixel-shader / liquid-surface feeling.
  */
  useEffect(() => {
    const canvas = waterCanvasRef.current;
    const hero = heroRef.current;

    if (!canvas || !hero) return;

    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) return;

    let animationFrame = null;
    let width = 0;
    let height = 0;
    let dpr = 1;
    let lastRippleTime = 0;
    let time = 0;

    const resizeCanvas = () => {
      const rect = hero.getBoundingClientRect();

      dpr = Math.min(window.devicePixelRatio || 1, 2);

      /*
        Low internal resolution = shader-like pixel texture.
        The DOM canvas remains full-size through CSS.
      */
      width = Math.max(320, Math.floor(rect.width / 2));
      height = Math.max(320, Math.floor(rect.height / 2));

      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const addRipple = (x, y, force = 1) => {
      ripplesRef.current.push({
        x,
        y,
        radius: 4,
        life: 1,
        force,
      });

      if (ripplesRef.current.length > 18) {
        ripplesRef.current.shift();
      }
    };

    const handlePointerMove = (event) => {
      const rect = hero.getBoundingClientRect();

      const x = ((event.clientX - rect.left) / rect.width) * width;
      const y = ((event.clientY - rect.top) / rect.height) * height;

      pointerRef.current.targetX = x;
      pointerRef.current.targetY = y;
      pointerRef.current.active = true;

      const now = performance.now();

      if (now - lastRippleTime > 42) {
        addRipple(x, y, 0.8);
        lastRippleTime = now;
      }
    };

    const handlePointerLeave = () => {
      pointerRef.current.active = false;
    };

    const draw = () => {
      time += 0.014;

      const pointer = pointerRef.current;

      pointer.x += (pointer.targetX - pointer.x) * 0.16;
      pointer.y += (pointer.targetY - pointer.y) * 0.16;

      ctx.clearRect(0, 0, width, height);

      const baseGradient = ctx.createLinearGradient(0, 0, width, height);
      baseGradient.addColorStop(0, "rgba(8, 73, 163, 0.08)");
      baseGradient.addColorStop(0.5, "rgba(74, 165, 101, 0.07)");
      baseGradient.addColorStop(1, "rgba(45, 48, 69, 0.06)");

      ctx.fillStyle = baseGradient;
      ctx.fillRect(0, 0, width, height);

      /*
        Pixel liquid field.
        We draw small cells instead of smooth full-res gradients.
      */
      const cell = width < 680 ? 16 : 20;

      for (let y = 0; y < height; y += cell) {
        for (let x = 0; x < width; x += cell) {
          const dx = x - pointer.x;
          const dy = y - pointer.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          const waveA = Math.sin(x * 0.025 + time * 2.2);
          const waveB = Math.cos(y * 0.027 - time * 1.8);
          const pointerWave = pointer.active
            ? Math.sin(distance * 0.075 - time * 13) *
            Math.max(0, 1 - distance / 260)
            : 0;

          let energy = (waveA + waveB) * 0.5 + pointerWave * 1.65;

          ripplesRef.current.forEach((ripple) => {
            const rx = x - ripple.x;
            const ry = y - ripple.y;
            const rd = Math.sqrt(rx * rx + ry * ry);
            const ring = Math.sin((rd - ripple.radius) * 0.15);
            const falloff = Math.max(0, 1 - Math.abs(rd - ripple.radius) / 70);

            energy += ring * falloff * ripple.life * ripple.force * 1.45;
          });

          const alpha = Math.max(0, Math.min(0.22, 0.055 + energy * 0.05));

          if (alpha > 0.035) {
            const blue = 120 + Math.floor(Math.max(0, energy) * 42);
            const green = 126 + Math.floor(Math.max(0, energy) * 34);

            ctx.fillStyle = `rgba(8, ${green}, ${blue}, ${alpha})`;
            ctx.fillRect(x, y, cell - 2, cell - 2);
          }
        }
      }

      /*
        Cursor contact highlight.
      */
      if (pointer.active) {
        const contactGlow = ctx.createRadialGradient(
          pointer.x,
          pointer.y,
          0,
          pointer.x,
          pointer.y,
          210
        );

        contactGlow.addColorStop(0, "rgba(255, 255, 255, 0.18)");
        contactGlow.addColorStop(0.22, "rgba(8, 73, 163, 0.14)");
        contactGlow.addColorStop(0.55, "rgba(74, 165, 101, 0.1)");
        contactGlow.addColorStop(1, "rgba(45, 48, 69, 0)");

        ctx.fillStyle = contactGlow;
        ctx.fillRect(0, 0, width, height);
      }

      ripplesRef.current = ripplesRef.current
        .map((ripple) => ({
          ...ripple,
          radius: ripple.radius + 3.2,
          life: ripple.life * 0.965,
        }))
        .filter((ripple) => ripple.life > 0.04);

      animationFrame = requestAnimationFrame(draw);
    };

    resizeCanvas();

    pointerRef.current.x = width * 0.68;
    pointerRef.current.y = height * 0.42;
    pointerRef.current.targetX = width * 0.68;
    pointerRef.current.targetY = height * 0.42;

    addRipple(width * 0.68, height * 0.42, 0.7);

    window.addEventListener("resize", resizeCanvas);
    hero.addEventListener("pointermove", handlePointerMove);
    hero.addEventListener("pointerleave", handlePointerLeave);

    animationFrame = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      hero.removeEventListener("pointermove", handlePointerMove);
      hero.removeEventListener("pointerleave", handlePointerLeave);

      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }

    };
  }, []);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      if (prefersReducedMotion) return;

      gsap.set(heroTextWrapRef.current, {
        opacity: 0,
        x: -80,
        rotate: 0,
        skewX: 0,
        skewY: 0,
        transformOrigin: "center center",
      });

      gsap.set(lottieWrapRef.current, {
        opacity: 0,
        x: 80,
        scale: 0.88,
        rotate: 0,
      });

      gsap.set(heroTitleRef.current, {
        opacity: 0,
        y: 70,
        scale: 1,
        rotate: 0,
        skewX: 0,
        skewY: 0,
        transformOrigin: "left center",
      });

      gsap.set(heroTextRef.current, {
        opacity: 0,
        y: 34,
        rotate: 0,
        skewX: 0,
        skewY: 0,
      });

      gsap.set(".pictorial-hero-action", {
        opacity: 0,
        y: 30,
      });

      gsap.set(galleryRef.current, {
        opacity: 0,
        y: 110,
        scale: 0.96,
      });

      gsap.set(galleryShellRef.current, {
        opacity: 0,
        y: 100,
        rotateX: 9,
        transformPerspective: 1800,
        transformOrigin: "center bottom",
      });

      gsap.set(".pictorial-side-frame", {
        opacity: 0,
        y: 70,
        scale: 0.9,
        rotateY: 0,
      });

      gsap.set(".pictorial-thumb", {
        opacity: 0,
        y: 40,
      });

      gsap.set(".pictorial-depth-card", {
        opacity: 0,
        y: 70,
        rotateX: 14,
        scale: 0.92,
      });

      const introTl = gsap.timeline({
        defaults: {
          ease: "power3.out",
        },
      });

      introTl
        .to(heroTextWrapRef.current, {
          opacity: 1,
          x: 0,
          rotate: 0,
          skewX: 0,
          skewY: 0,
          duration: 0.85,
        })
        .to(
          lottieWrapRef.current,
          {
            opacity: 1,
            x: 0,
            scale: 1,
            rotate: 0,
            duration: 1.1,
          },
          "-=0.7"
        )
        .to(
          heroTitleRef.current,
          {
            opacity: 1,
            y: 0,
            scale: 1,
            rotate: 0,
            skewX: 0,
            skewY: 0,
            duration: 1,
          },
          "-=0.45"
        )
        .to(
          heroTextRef.current,
          {
            opacity: 1,
            y: 0,
            rotate: 0,
            skewX: 0,
            skewY: 0,
            duration: 0.75,
          },
          "-=0.55"
        )
        .to(
          ".pictorial-hero-action",
          {
            opacity: 1,
            y: 0,
            duration: 0.55,
            stagger: 0.08,
          },
          "-=0.35"
        );

      /*
        Only the Lottie floats slightly.
        The text remains horizontal and does not slope on scroll.
      */
      gsap.to(lottieWrapRef.current, {
        y: -76,
        scale: 1.035,
        rotate: 0,
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "55% top",
          scrub: 1.35,
        },
      });

      const galleryRevealTl = gsap.timeline({
        scrollTrigger: {
          trigger: galleryRef.current,
          start: "top 84%",
          end: "top 22%",
          scrub: 1.05,
          invalidateOnRefresh: true,
        },
      });

      galleryRevealTl
        .to(
          galleryRef.current,
          {
            opacity: 1,
            y: 0,
            scale: 1,
            ease: "none",
          },
          0
        )
        .to(
          galleryShellRef.current,
          {
            opacity: 1,
            y: 0,
            rotateX: 0,
            ease: "none",
          },
          0.12
        )
        .to(
          ".pictorial-side-frame",
          {
            opacity: 1,
            y: 0,
            scale: 1,
            stagger: 0.08,
            ease: "none",
          },
          0.25
        )
        .to(
          ".pictorial-depth-card",
          {
            opacity: 1,
            y: 0,
            rotateX: 0,
            scale: 1,
            stagger: 0.07,
            ease: "none",
          },
          0.3
        )
        .to(
          ".pictorial-thumb",
          {
            opacity: 1,
            y: 0,
            stagger: {
              each: 0.035,
              from: "center",
            },
            ease: "none",
          },
          0.42
        );

      /*
        3D award-site style gallery immersion.
        As the user scrolls, the active image moves toward them,
        side images bend away, and the gallery gains depth.
      */
      const immersiveTl = gsap.timeline({
        scrollTrigger: {
          trigger: galleryShellRef.current,
          start: "top 72%",
          end: "bottom 18%",
          scrub: 1.35,
          invalidateOnRefresh: true,
        },
      });

      immersiveTl
        .to(
          activeFrameRef.current,
          {
            y: -42,
            scale: 1.045,
            rotateX: 0,
            z: 90,
            ease: "none",
          },
          0
        )
        .to(
          imageStageRef.current,
          {
            scale: 1.08,
            y: -24,
            rotateX: -3.5,
            transformPerspective: 1800,
            transformOrigin: "center center",
            ease: "none",
          },
          0
        )
        .to(
          ".pictorial-previous-frame",
          {
            x: -30,
            rotateY: 12,
            rotateX: 2,
            scale: 0.94,
            z: -120,
            ease: "none",
          },
          0
        )
        .to(
          ".pictorial-next-frame",
          {
            x: 30,
            rotateY: -12,
            rotateX: 2,
            scale: 0.94,
            z: -120,
            ease: "none",
          },
          0
        )
        .to(
          ".pictorial-depth-card",
          {
            y: -52,
            rotateX: -7,
            scale: 1.04,
            stagger: 0.03,
            ease: "none",
          },
          0
        )
        .to(
          ".pictorial-gallery-orbit",
          {
            rotate: 8,
            scale: 1.08,
            ease: "none",
          },
          0
        );

      gsap.to(".pictorial-floating-shape", {
        y: -160,
        rotate: 24,
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 1.6,
        },
      });

      ScrollTrigger.refresh();
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    if (isPaused) return;

    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % images.length);
    }, 4200);

    return () => clearInterval(timer);
  }, [isPaused, images.length]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "ArrowRight") {
        goNext();
      }

      if (event.key === "ArrowLeft") {
        goPrev();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goNext, goPrev]);

  useEffect(() => {
    const stage = imageStageRef.current;
    const progress = progressRef.current;
    const frame = activeFrameRef.current;

    if (!stage) return;

    gsap.fromTo(
      stage,
      {
        opacity: 0,
        scale: 0.965,
        y: 34,
        rotateX: 6,
        filter: "blur(10px)",
        transformPerspective: 1800,
      },
      {
        opacity: 1,
        scale: 1,
        y: 0,
        rotateX: 0,
        filter: "blur(0px)",
        duration: 0.72,
        ease: "power3.out",
      }
    );

    if (frame) {
      gsap.fromTo(
        frame,
        {
          boxShadow:
            "0 24px 80px rgba(8, 73, 163, 0.12), 0 0 0 rgba(74, 165, 101, 0)",
        },
        {
          boxShadow:
            "0 34px 110px rgba(8, 73, 163, 0.16), 0 18px 80px rgba(74, 165, 101, 0.14)",
          duration: 0.8,
          ease: "power3.out",
        }
      );
    }

    gsap.fromTo(
      ".active-image-meta",
      {
        opacity: 0,
        y: 26,
      },
      {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.08,
        ease: "power3.out",
      }
    );

    gsap.fromTo(
      ".pictorial-depth-card",
      {
        opacity: 0,
        y: 40,
        rotateX: 10,
      },
      {
        opacity: 1,
        y: 0,
        rotateX: 0,
        duration: 0.65,
        stagger: 0.06,
        ease: "power3.out",
      }
    );

    if (progress) {
      gsap.fromTo(
        progress,
        {
          scaleX: 0,
          transformOrigin: "left center",
        },
        {
          scaleX: 1,
          duration: 4.2,
          ease: "none",
        }
      );
    }
  }, [activeIndex]);

  return (
    <section
      id="pictorial"
      ref={sectionRef}
      className="relative overflow-hidden bg-white text-gray-900 dark:bg-gray-950 dark:text-white"
    >
      <style>
        {`
          .pictorial-water-canvas {
            image-rendering: pixelated;
            image-rendering: crisp-edges;
          }

          .pictorial-perspective-scene {
            perspective: 1800px;
            transform-style: preserve-3d;
          }

          .pictorial-preserve-3d {
            transform-style: preserve-3d;
          }

          .pictorial-hero-flat,
          .pictorial-hero-flat * {
            rotate: 0deg;
            skew: 0deg;
            transform-style: flat;
          }

          .pictorial-subtle-grid {
            background-image:
              linear-gradient(rgba(8, 73, 163, 0.08) 1px, transparent 1px),
              linear-gradient(90deg, rgba(74, 165, 101, 0.08) 1px, transparent 1px);
            background-size: 58px 58px;
            mask-image: radial-gradient(circle at 50% 35%, black, transparent 72%);
          }

          .pictorial-glass-edge {
            box-shadow:
              inset 0 1px 0 rgba(255, 255, 255, 0.18),
              inset 0 -1px 0 rgba(255, 255, 255, 0.05);
          }

          .pictorial-gallery-shell {
            transform-style: preserve-3d;
          }

          .pictorial-side-frame,
          .pictorial-main-frame {
            transform-style: preserve-3d;
            backface-visibility: hidden;
          }

          .pictorial-thumb::-webkit-scrollbar {
            display: none;
          }

          .pictorial-marquee-track {
            animation: pictorial-marquee 42s linear infinite;
            will-change: transform;
          }

          .pictorial-marquee-wrap:hover .pictorial-marquee-track {
            animation-play-state: paused;
          }

          @keyframes pictorial-marquee {
            from {
              transform: translateX(0);
            }

            to {
              transform: translateX(-50%);
            }
          }
        `}
      </style>

      {/* HERO */}
      <div
        ref={heroRef}
        className="relative flex min-h-screen items-center overflow-hidden px-4 py-28 sm:px-6 lg:px-8"
      >
        {/* WATER SHADER BACKGROUND */}
        <canvas
          ref={waterCanvasRef}
          className="pictorial-water-canvas pointer-events-none absolute inset-0 h-full w-full opacity-80 mix-blend-normal dark:opacity-90"
          aria-hidden="true"
        />

        <div className="pointer-events-none absolute inset-0">
          <div className="pictorial-subtle-grid absolute inset-0 opacity-70 dark:opacity-40" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#f4f3ee] to-transparent dark:from-black" />
        </div>

        <div className="container relative z-10 mx-auto">
          <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
            {/* LEFT: HERO TEXT */}
            <div
              ref={heroTextWrapRef}
              className="pictorial-hero-flat order-1"
              style={{
                rotate: "0deg",
                skew: "0deg",
                perspective: "none",
                transformStyle: "flat",
                willChange: "auto",
              }}
            >

              <h1
                ref={heroTitleRef}
                className="max-w-4xl text-[clamp(3.4rem,10vw,9rem)] font-normal leading-[0.88] tracking-[-0.075em] text-gray-950 dark:text-white"
                style={{
                  rotate: "0deg",
                  skew: "0deg",
                  perspective: "none",
                  transformStyle: "flat",
                  willChange: "auto",
                }}
              >
                <span className="brand-accent">Pictorial</span>
                <span className="editorial-italic brand-accent-green block">
                  Showcase
                </span>
              </h1>

              <p
                ref={heroTextRef}
                className="mt-8 max-w-2xl text-base leading-snug tracking-[-0.025em] text-gray-600 dark:text-gray-300 sm:text-lg md:text-xl"
                style={{
                  rotate: "0deg",
                  skew: "0deg",
                  perspective: "none",
                  transformStyle: "flat",
                  willChange: "auto",
                }}
              >
                Images from work, projects, products, and moments around the
                systems William builds.
              </p>

              <div
                className="mt-10 flex flex-col gap-4 sm:flex-row"
                style={{
                  rotate: "0deg",
                  skew: "0deg",
                  perspective: "none",
                  transformStyle: "flat",
                  willChange: "auto",
                }}
              >
                <a
                  href="#pictorial-gallery"
                  className="brand-button pictorial-hero-action inline-flex items-center justify-center rounded-full px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] transition-all duration-300"
                >
                  Explore Gallery
                  <i className="bx bx-down-arrow-alt ml-2 text-xl"></i>
                </a>

                <a
                  href="/contact"
                  className="brand-button-outline pictorial-hero-action inline-flex items-center justify-center rounded-full border bg-transparent px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] transition-all duration-300"
                >
                  Work With Me
                </a>
              </div>
            </div>

            {/* RIGHT: BORDERLESS LOTTIE */}
            <div
              ref={lottieWrapRef}
              className="relative order-2 flex min-h-[320px] items-center justify-center lg:min-h-[560px]"
            >
              <Lottie
                animationData={designAnimation}
                loop
                autoplay
                className="relative z-10 h-[320px] w-full max-w-[520px] sm:h-[430px] lg:h-[560px]"
              />
            </div>
          </div>
        </div>
      </div>

      {/* GALLERY */}
      <div
        id="pictorial-gallery"
        ref={galleryRef}
        className="relative px-4 pb-24 pt-10 sm:px-6 lg:px-8"
      >
        <div className="pointer-events-none absolute inset-0">
          <div className="pictorial-gallery-orbit absolute left-1/2 top-1/2 h-[720px] w-[720px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-black/5 dark:border-white/5" />
        </div>

        <div className="container relative z-10 mx-auto">
          <div className="mb-10 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>

              <h2 className="mt-5 text-4xl font-normal leading-none tracking-[-0.06em] text-gray-950 dark:text-white sm:text-5xl md:text-6xl">
                Visual <span className="brand-accent">Moments</span>
              </h2>

              <p className="mt-4 max-w-2xl leading-snug tracking-[-0.02em] text-gray-600 dark:text-gray-400">
                Move through selected images from the work and the context
                around it.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setIsPaused((prev) => !prev)}
                className="brand-button-outline inline-flex h-12 items-center justify-center rounded-full border bg-[#fbfaf6] px-5 text-[11px] font-semibold uppercase tracking-[0.16em] transition-all duration-300 dark:bg-[#131416]"
                aria-label={isPaused ? "Resume carousel" : "Pause carousel"}
              >
                {isPaused ? "Play" : "Pause"}
              </button>

              <button
                type="button"
                onClick={goPrev}
                className="flex h-12 w-12 items-center justify-center rounded-full border border-black/10 bg-[#fbfaf6] text-gray-800 transition-all duration-300 hover:border-[var(--brand-blue)] hover:text-[var(--brand-blue)] dark:border-white/10 dark:bg-[#131416] dark:text-white dark:hover:border-white"
                aria-label="Previous image"
              >
                <i className="bx bx-left-arrow-alt text-2xl"></i>
              </button>

              <button
                type="button"
                onClick={goNext}
                className="flex h-12 w-12 items-center justify-center rounded-full border border-black/10 bg-[#fbfaf6] text-gray-800 transition-all duration-300 hover:border-[var(--brand-green)] hover:text-[var(--brand-green)] dark:border-white/10 dark:bg-[#131416] dark:text-white dark:hover:border-white"
                aria-label="Next image"
              >
                <i className="bx bx-right-arrow-alt text-2xl"></i>
              </button>
            </div>
          </div>

          <div
            ref={galleryShellRef}
            className="pictorial-gallery-shell pictorial-marquee-wrap relative -mx-4 overflow-hidden bg-white sm:-mx-6 lg:-mx-8"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div className="overflow-hidden px-2 pb-8">
              <div className="pictorial-marquee-track flex w-max gap-[6px]">
                {[...images, ...images].map((image, index) => {
                  const realIndex = index % images.length;
                  const isActive = realIndex === activeIndex && index < images.length;

                  return (
                    <button
                      key={`${image.id}-${index}`}
                      ref={isActive ? activeFrameRef : null}
                      type="button"
                      onClick={() => goToImage(realIndex)}
                      className={`pictorial-thumb group relative h-[72vh] min-h-[520px] w-[78vw] shrink-0 overflow-hidden bg-[#f3f3f3] transition-all duration-700 sm:w-[44vw] md:w-[31vw] lg:w-[23.75vw] xl:w-[23.8vw] ${
                        isActive ? "opacity-100" : "opacity-95"
                      }`}
                      aria-label={`View ${image.title}`}
                    >
                      <div
                        ref={isActive ? imageStageRef : null}
                        className="absolute inset-0 overflow-hidden"
                      >
                        <img
                          src={image.src}
                          alt={image.title}
                          loading="lazy"
                          className="absolute bottom-0 left-1/2 h-[82%] w-full -translate-x-1/2 object-cover object-bottom saturate-110 contrast-105 transition duration-700 group-hover:scale-[1.035]"
                        />
                      </div>

                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#f7f7f7]/70 via-transparent to-transparent opacity-70" />

                      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-36 bg-gradient-to-t from-black/40 to-transparent opacity-0 transition duration-500 group-hover:opacity-100" />

                      <div className="absolute bottom-6 left-6 right-6 translate-y-4 opacity-0 transition duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/75">
                          {image.category}
                        </p>

                        <h3 className="mt-2 text-2xl font-normal leading-none tracking-[-0.05em] text-white">
                          {image.title}
                        </h3>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-7 flex items-center justify-center gap-2">
            {images.map((image, index) => {
              const isActive = index === activeIndex;

              return (
                <button
                  key={image.id}
                  type="button"
                  onClick={() => goToImage(index)}
                  className={`relative h-2.5 w-2.5 rounded-full transition-all duration-300 ${isActive ? "bg-red-500" : "bg-black/15 hover:bg-black/35"
                    }`}
                  aria-label={`Go to ${image.title}`}
                >
                  {isActive && (
                    <span
                      ref={progressRef}
                      className="absolute inset-0 rounded-full bg-red-500"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

    </section>
  );
};

export default Pictorial;
